<script setup lang="ts">
import { DialogContent, type DialogContentEmits, type DialogContentProps, DialogOverlay, DialogPortal, useForwardPropsEmits } from 'radix-vue';
import { computed, type HTMLAttributes } from 'vue';
import { cn } from '@/lib/utils';

const props = defineProps<
    DialogContentProps & {
        class?: HTMLAttributes['class'];
        // Reserves the overlay's scrollbar gutter unconditionally, even while this dialog's content
        // currently fits without scrolling - for the rare dialog whose content can grow past the
        // viewport mid-interaction (e.g. a calendar popover opening inside one that's already close
        // to full height). Off by default: reserving it unconditionally shifts every dialog a few
        // pixels off true center permanently, a worse trade for the near-all that never scroll at all.
        reserveScrollbarGutter?: boolean;
    }
>();
const emits = defineEmits<DialogContentEmits>();

const delegatedProps = computed(() => {
    const { class: _, reserveScrollbarGutter: __, ...delegated } = props;

    return delegated;
});

const forwarded = useForwardPropsEmits(delegatedProps, emits);
</script>

<template>
    <DialogPortal>
        <!-- Deliberately no `data-[state=closed]:*` exit animation on either the overlay or the content.
             radix-vue's Presence only suspends the unmount when the closing element has an animation-name,
             and it then waits for that animation's `animationend` before unmounting. Any close that
             interrupts the still-running enter animation never produces that event, so Presence stays in
             `unmountSuspended` forever — and with it DismissableLayer's `body.pointer-events: none`,
             useBodyScrollLock's `body.overflow: hidden` and hideOthers' `#app[aria-hidden]`, which are all
             undone by unmount cleanups. The result is a permanently unclickable page from nothing worse
             than opening a dialog and immediately clicking Cancel. With no exit animation Presence takes
             its synchronous UNMOUNT path instead, so the cleanups can never be skipped.
             The enter animation is safe to keep: the open path never waits on an animation event. -->
        <DialogOverlay
            class="fixed inset-0 z-[2000] grid place-items-center overflow-y-auto bg-background/80 backdrop-blur-sm data-[state=open]:animate-in data-[state=open]:fade-in-0 py-6"
            :style="reserveScrollbarGutter ? 'scrollbar-gutter: stable' : undefined"
        >
            <DialogContent
                :class="
                    cn(
                        'sm:max-w-[480px] relative z-50 grid w-full max-w-lg gap-y-6 gap-x-4 border border-gray-200 bg-white p-8 shadow-lg duration-200 data-[state=open]:animate-in data-[state=open]:fade-in-0 data-[state=open]:zoom-in-95 sm:rounded-lg md:w-full',
                        props.class
                    )
                "
                v-bind="forwarded"
                @interact-outside="(event) => {
                    // Prevent closing the dialog when clicking on the toast close button or container
                    if ((event.target as HTMLElement)?.closest('.Vue-Toastification__close-button') || (event.target as HTMLElement)?.closest('.Vue-Toastification__toast')) {
                        event.preventDefault();
                    }
                }"
            >
                <slot />
            </DialogContent>
        </DialogOverlay>
    </DialogPortal>
</template>
